import { createContext, useContext, useState } from "react"

import ManyKeysMap from "many-keys-map"
import { tryFocusFirstChild } from "./helpers"

export const Directions = ["UP", "DOWN", "LEFT", "RIGHT"] as const
export type Direction = (typeof Directions)[number]
export type Dimension = [number, number]
type Nullable<T> = T | null

export class FocusManager<
  T extends HTMLElement = HTMLElement,
> extends ManyKeysMap<Dimension, T> {
  m: number
  n: number
  active: Dimension | null
  constructor(m?: number, n?: number) {
    super()
    this.m = m || 0
    this.n = n || 0
    this.active = null
  }
  setDimensions(m: number, n: number) {
    this.m = m
    this.n = n
  }
  register(row: number, col: number, el: Nullable<T>) {
    if (el) {
      this.set([row, col], el)
    } else {
      this.delete([row, col])
    }
  }
  getElement() {
    if (this.active === null) return
    return this.get(this.active)
  }
  setActive(d: Dimension | null) {
    this.active = d
  }
  isActive(d: Dimension): boolean {
    if (this.active === null) return false
    return d[0] === this.active[0] && d[1] === this.active[1]
  }
  getNextInDirection(direction: Direction) {
    // if no current active, quit
    if (this.active === null) return null
    const next = this.next(this.active, direction)
    // if direction didn't change, quit
    if (this.isActive(next)) return null
    const nextEl = this.get(next)
    // if we don't have an el in next direction, quit
    if (nextEl === undefined) return null
    // finally, set current active dir & return el
    this.setActive(next)
    return this.get(next)
  }
  private next(origin: Dimension, direction: Direction): Dimension {
    const [om, on] = origin
    switch (direction) {
      case "UP": {
        return [Math.max(0, om - 1), on]
      }
      case "DOWN": {
        return [Math.min(this.m - 1, om + 1), on]
      }
      case "LEFT": {
        return [om, Math.max(0, on - 1)]
      }
      case "RIGHT": {
        return [om, Math.min(this.n - 1, on + 1)]
      }
    }
  }
}

export const FocusManagementContext = createContext<FocusManager | undefined>(
  undefined,
)

export function useFocusManagement<T extends HTMLElement = HTMLElement>() {
  const fm = useContext(FocusManagementContext)
  if (fm === undefined) {
    throw new Error("useFocusManagement not in scope")
  }

  const [focused, setFocused] = useState<boolean>(() => false)
  const [editing, setEditing] = useState<boolean>(() => false)

  let focusNextInDirection = (direction: Direction) => {
    const next = fm.getNextInDirection(direction)
    if (next) {
      next.focus()
    }
  }

  const onKeyDown: React.KeyboardEventHandler<T> = (evt) => {
    switch (evt.key) {
      case "ArrowUp":
        evt.preventDefault()
        focusNextInDirection("UP")
        break
      case "ArrowDown":
        evt.preventDefault()
        focusNextInDirection("DOWN")
        break
      case "ArrowRight":
        evt.preventDefault()
        focusNextInDirection("RIGHT")
        break
      case "ArrowLeft":
        evt.preventDefault()
        focusNextInDirection("LEFT")
        break
      case "Enter":
        evt.preventDefault()
        const activeEl = fm.getElement()
        tryFocusFirstChild(activeEl)
        break
      default:
        break
    }
  }

  const getManagerProps = (rowIndex: number, columnIndex: number) => {
    const row = rowIndex
    const col = columnIndex
    return {
      ref: (el: T | null) => fm.register(row, col, el),
      onKeyDown,
      onFocus() {
        setFocused(true)
        fm.setActive([row, col])
      },
      onBlur() {
        setFocused(false)
      },
      tabIndex: focused ? 0 : -1,
    }
  }

  const getInputProps = () => {
    return {
      onFocus(_evt?: React.FocusEvent) {
        setEditing(true)
      },
      onBlur(_evt: React.FocusEvent) {
        setEditing(false)
      },
      onKeyDown(evt: React.KeyboardEvent) {
        if (editing) evt.stopPropagation()
        if (evt.key === "Escape") {
          setEditing(false)
        }
      },
    }
  }

  return { getManagerProps, getInputProps, fm } as const
}
